// Lista de estado del email en el signup — ÚNICA forma de mostrar el estado del
// email, con severidad estandarizada. Cada `EmailVerdict` (+ los flags de la
// máquina `checking`/`warning`) mapea a una LÍNEA con ícono + color por severidad:
//
//   checking → spinner neutro  "Verificando…"          (consultando base/dominio)
//   warning  → ⚠ ámbar          "No pudimos verificar…"  (fallo transitorio / fail-closed)
//   error    → ✕ roja           formato/TLD/typo/existe/no-entregable
//   success  → ✓ verde          "Este email está disponible."
//
// La coincidencia email/confirmación la muestra <EmailMatchHint> (misma familia
// visual: ✓ verde / ✕ roja). El `switch` es EXHAUSTIVO (assertNever): agregar un
// verdict sin manejarlo NO COMPILA.

import type { ReactNode } from 'react';
import { CheckIcon, XIcon, WarnIcon, Spinner } from './PasswordChecklist';
import {
  assertNever,
  EMAIL_INVALID_MSG,
  EMAIL_EXISTS_MSG,
  tldErrorMsg,
  undeliverableMsg,
  type EmailVerdict,
} from '@/lib/emailValidation';
import { ColoredDomain } from './EmailTypoSuggestion';

type Severity = 'error' | 'warning' | 'checking' | 'success';

function StatusLine({ severity, text }: { severity: Severity; text: ReactNode }): JSX.Element {
  const cls =
    severity === 'error'
      ? 'text-error font-medium'
      : severity === 'warning'
        ? 'text-warning font-medium'
        : severity === 'success'
          ? 'text-success font-medium'
          : 'text-text-muted';
  const icon =
    severity === 'error' ? (
      <XIcon />
    ) : severity === 'warning' ? (
      <WarnIcon />
    ) : severity === 'success' ? (
      <CheckIcon />
    ) : (
      <Spinner />
    );
  return (
    <p aria-live="polite" className={`flex items-center gap-2 text-xs transition-colors ${cls}`}>
      {icon}
      <span>{text}</span>
    </p>
  );
}

interface EmailStatusProps {
  verdict: EmailVerdict;
  touched: boolean;
  /** La máquina está consultando (existencia/dominio) → spinner. */
  checking: boolean;
  /** No se pudo verificar (fallo transitorio / fail-closed) → warning ámbar. */
  warning: string | null;
}

export default function EmailStatus({
  verdict,
  touched,
  checking,
  warning,
}: EmailStatusProps): JSX.Element | null {
  // El spinner gana: mientras verifica, es lo único relevante (el check corre al
  // blur, así que `touched` ya es true, pero no dependemos de eso para el spinner).
  if (checking) return <StatusLine severity="checking" text="Verificando…" />;
  if (!touched) return null;
  if (warning) return <StatusLine severity="warning" text={warning} />;

  switch (verdict.kind) {
    case 'invalid_format':
      return <StatusLine severity="error" text={EMAIL_INVALID_MSG} />;
    case 'bad_tld':
      return <StatusLine severity="error" text={tldErrorMsg(verdict.tld)} />;
    case 'typo':
      return (
        <StatusLine
          severity="error"
          text={
            <>
              ¿Quisiste poner{' '}
              <ColoredDomain
                typed={verdict.suggestion.typedDomain}
                suggested={verdict.suggestion.suggestedDomain}
              />
              ?
            </>
          }
        />
      );
    case 'exists':
      return <StatusLine severity="error" text={EMAIL_EXISTS_MSG} />;
    case 'undeliverable':
      return <StatusLine severity="error" text={undeliverableMsg()} />;
    case 'ok':
      return <StatusLine severity="success" text="Correo disponible" />;

    // coincidencia → <EmailMatchHint>; estos no tienen línea propia acá.
    case 'empty':
    case 'mismatch':
    case 'unverified':
    case 'checking':
      return null;

    default:
      return assertNever(verdict);
  }
}
